<template>
  <div class="sales-base">
    <div class="d-lg-flex justify-content-between mb-4">
      <h5 class="text-semibold">Visão geral de vendas</h5>
      <snow-button
        size="sm"
        variant="borderless"
        :text="hideValues ? 'Mostrar valores' : 'Ocultar valores'"
        :icon="hideValues ? 'ph ph-eye' : 'ph ph-eye-slash'"
        @click="hideValues = !hideValues"
      />
    </div>
    <template v-if="$permissions.hasPermission('visualizar-valores')">
      <div class="row">
        <div class="col-lg-6 mb-5">
          <info-card
            title="Valor total"
            icon="ph ph-currency-circle-dollar"
            :value="formatMoney(totalAmount || 0)"
            :blur="hideValues"
          />
        </div>

        <div class="col-lg-6 mb-5">
          <info-card
            title="Quantidade total"
            icon="ph ph-users"
            :value="pagination.count || 0"
            :blur="hideValues"
            prefix=""
          />
        </div>
      </div>
    </template>

    <snow-data-table-header
      ref="dataTableHeader"
      :add="false"
      :exportOptions="exportOptions"
      :dates="filters.dates"
      @searching="onSearch"
      @open-filters="filterModalValue = true"
      @select="addressDialog.value = true"
      @dates="onDatesChange"
    />

    <div class="my-9 text-center" v-if="loading">
      <b-spinner variant="primary" />
    </div>

    <snow-data-table v-else :columns="columns" :data="sales">
      <template #no-content-actions>
        <div class="d-flex flex-column gap-4">
          <div class="bg-2 p-4 rounded-16">
            <div class="d-flex gap-3">
              <i class="ph ph-rocket-launch fs-32" />
              <div class="d-flex flex-column">
                <h6 class="text-semibold">Criar um novo produto</h6>
                <h6 class="text-dark-40">
                  Se não tiver criado nenhum, va em novo produto e comece a
                  criar o seu.
                </h6>
              </div>
            </div>
          </div>
          <div class="bg-2 p-4 rounded-16">
            <div class="d-flex gap-3">
              <i class="ph ph-clock fs-32" />
              <div class="d-flex flex-column">
                <h6 class="text-semibold">Esperar por vendas</h6>
                <h6 class="text-dark-40">
                  Se seu produto já publicado, não vai demorar em aparecer
                  vendas.
                </h6>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #comprador="{item}">
        <div>
          <h6>
            {{ item.shopper_name }}
          </h6>
          <h6 class="shopper-email text-dark-40">
            {{ item.shopper_email }}
          </h6>
        </div>
      </template>
    </snow-data-table>

    <div class="d-flex justify-content-end mt-5" v-if="sales.length">
      <snow-paginator
        v-show="!loading"
        v-model="currentPage"
        :pagination="pagination"
        @change-page="dispatch"
      />
    </div>

    <responsive-modal
      v-model="filterModalValue"
      title="Filtrar"
      description="Selecione os filtros e aplique"
    >
      <template>
        <component v-if="filterModal" :is="filterModal" v-model="filters" />
        <div class="snow-submit-buttons mt-7">
          <snow-button
            size="lg"
            text="Cancelar"
            variant="gray"
            @click="filterModalValue = false"
          />
          <snow-button size="lg" text="Aplicar" @click="applyFilters" />
        </div>
      </template>
    </responsive-modal>

    <responsive-modal v-model="modal.value" :title="modal.title">
      <component
        v-if="modal.component"
        :is="modal.component"
        :item="itemToShow"
        @refresh="refresh"
        @close="closeModal"
      />
    </responsive-modal>

    <bp-dialog
      v-model="addressDialog.value"
      v-bind="addressDialog"
      @action="exportData(undefined)"
      @secondary-action="exportData(true)"
    />
  </div>
</template>

<script>
import {
  SALE_PAY_METHOD_NAME_MAP,
  SALE_STATUS_BADGE_MAP,
  SALE_STATUS_NAME_MAP
} from "@/constants/sales.js";
import { clearObject, formatMoney } from "@/functions/helpers";
import moment from "moment";
import { mapState } from "vuex";

import AddressConfirmation from "@/components/sale/AddressConfirmation.vue";
import ChangeSubscription from "@/components/sale/ChangeSubscription";
import SaleDetail from "@/components/sale/Details";
import ShowBillet from "@/components/sale/ShowBillet";
import TryAgain from "@/components/sale/TryAgain";
import InfoCard from "@/components/InfoCard.vue";

export default {
  components: {
    TryAgain,
    SaleDetail,
    ShowBillet,
    ChangeSubscription,
    AddressConfirmation,
    InfoCard
  },
  data() {
    return {
      filterModalValue: false,
      filterModal: null,
      filters: {
        status: ["paid", "completed"],
        payment_method: [],
        product_id: [],
        dates: ["", ""],
        is_order_bump: undefined,
        is_upsell: undefined,
        current_install: undefined,
        installments: undefined,
        is_trial: undefined,
        has_coupon: ""
      },
      filterComponent: null,
      search: "",
      currentPage: 1,
      loading: false,
      product_type: undefined,
      hideValues: false,

      modal: {
        value: false,
        component: undefined
      },
      itemToShow: {},

      addressDialog: {
        value: false,
        text: "Deseja exportar o arquivo com o endereço do comprador?",
        icon: "fa fa-circle-exclamation",
        buttonText: "Não",
        secondaryButtonText: "Sim"
      }
    };
  },
  computed: {
    ...mapState("sale", ["sales", "totalAmount", "pagination"]),
    ...mapState("integration", ["coproducerProducts"]),
    columns() {
      return [
        {
          name: "ID Transação",
          field: "id",
          transform: v => `BP${v}`
        },
        {
          name: "Produto",
          field: "product_name"
        },
        {
          name: "Comprador"
        },
        {
          name: "Data",
          field: "created_at",
          transform: v => new Date(v).toLocaleDateString("pt-BR")
        },
        {
          name: "Pagamento",
          field: "payment_method",
          transform: v => SALE_PAY_METHOD_NAME_MAP[v]
        },
        {
          name: "Parcelamento",
          field: "installments",
          transform: this.formatInstallments
        },
        {
          name: "Cobrança",
          field: "current_install",
          transform: this.formatCurrentInstallment
        },
        {
          name: "Comissão",
          field: "amount",
          class: { blur: this.hideValues },
          transform: v => {
            if (this.$permissions.hasPermission("visualizar-valores")) {
              return `R$ ${formatMoney(v)}`;
            }
            return "-";
          }
        },
        {
          name: "Status",
          field: "status",
          class: this.formatStatusBadge,
          transform: this.formatStatusName
        },
        {
          name: "",
          type: "menu",
          options: this.itemActions
        }
      ];
    },
    exportOptions() {
      return {
        action: "sale/getCSV",
        filters: () => this.filters,
        filename: "sales",
        label: "Exportar",
        confirmation: true,
        permission: "exportar-vendas"
      };
    }
  },
  methods: {
    formatMoney,

    getFormattedFilters() {
      const filters = {
        ...this.filters,
        product_type: this.product_type
      };

      if (!filters.is_order_bump) delete filters.is_order_bump;
      if (!filters.is_upsell) delete filters.is_upsell;
      if (!filters.is_trial) delete filters.is_trial;

      if (filters.dates[0]) {
        filters.created_at_after = filters.dates[0];
      }
      if (filters.dates[1]) {
        filters.created_at_before = filters.dates[1];
      }
      delete filters.dates;

      if (filters.has_coupon === "") {
        delete filters.has_coupon;
      } else {
        filters.has_coupon = filters.has_coupon === "has-coupon";
      }

      return filters;
    },
    applyFilters() {
      this.filterModalValue = false;
      this.$store.commit("sale/setSalesFilters", {
        filters: { ...this.filters },
        product_type: this.product_type
      });
      this.refresh();
    },
    refresh() {
      this.currentPage = 1;
      this.search = "";
      this.dispatch();
    },
    onSearch(search) {
      this.search = search;
      this.dispatch();
    },
    onDatesChange(dates) {
      this.filters = { ...this.filters, dates };

      this.$store.commit("sale/setSalesFilters", {
        filters: this.filters,
        product_type: this.product_type
      });

      this.refresh();
    },
    dispatch(offset) {
      this.loading = true;

      const params = {
        offset,
        search: this.search,
        ...this.getFormattedFilters()
      };

      this.$store
        .dispatch("sale/get", clearObject(params))
        .then(response => {
          // Se total_amount não vier na resposta do GET e for a primeira página, busca separadamente
          if (
            offset === undefined &&
            (!response || response.total_amount === undefined)
          ) {
            const totalParams = { ...params };
            delete totalParams.offset;
            delete totalParams.search;
            return this.$store.dispatch(
              "sale/getTotalSales",
              clearObject(totalParams)
            );
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportData(address_export = undefined) {
      const filters = { ...this.getFormattedFilters(), address_export };

      this.$refs.dataTableHeader.$refs.exportButton.exportData(filters);
      this.addressDialog.value = false;
    },

    formatInstallments(installments) {
      if (!installments) return "-";
      return installments == 1 ? "à vista" : installments + "x";
    },
    formatCurrentInstallment(_, item) {
      if (item.charge_type === "signature" && item.subscription)
        return (
          item.subscription.current_cycle +
          " de " +
          (this.isAutoRenew(item) ? "" : item.subscription.cycles)
        );

      if (["card", "two_card"].includes(item.payment_method)) return "1 de 1";

      if (!item.current_install || !item.installments) return "-";

      return item.current_install + " de " + item.installments;
    },
    formatStatusBadge(status) {
      return SALE_STATUS_BADGE_MAP[status];
    },
    formatStatusName(status) {
      return SALE_STATUS_NAME_MAP[status];
    },

    showBillet(item) {
      this.modal.component = ShowBillet;
      this.itemToShow = item;
      this.modal.title = "Boleto";
      this.modal.value = true;
    },
    changeSubscription(item) {
      this.modal.component = ChangeSubscription;
      this.modal.title = "Configurar pagamento";
      this.itemToShow = item;
      this.modal.value = true;
    },
    tryAgain(item) {
      this.modal.component = TryAgain;
      this.modal.title = "Tentar novamente";
      this.itemToShow = item;
      this.modal.value = true;
    },
    openDetail(item) {
      this.$router.push({ name: "sale-details", params: { id: item.id } });
    },

    submitRefund(id) {
      this.$store
        .dispatch("sale/refund", id)
        .then(() => {
          this.$message.success("O reembolso foi enviado com sucesso.");
          this.dispatch();
        })
        .catch(() => {
          this.$message.error("Erro ao enviar reembolso");
        });
    },
    cancelSubscription(id) {
      this.$store
        .dispatch("subscriptions/cancel", id)
        .then(() => {
          this.$message.success("Assinatura cancelada.");
          this.refresh();
        })
        .catch(() => {
          this.$message.error("Erro ao realizar cancelamento");
        });
    },
    rePix(item) {
      window.axios
        .post(`/payment/re-pix/${item.id}`)
        .then(() => {
          this.$message.success("Pix reenviado.");
        })
        .catch(() => {
          this.$message.error("Erro ao reenviar pix.");
        });
    },
    openReceipt(item) {
      window.open(
        `https://api.zeuspay.com.br/v1/reports/${item.code}`,
        "_blank"
      );
    },

    allowRefund(item) {
      let refundable = ["paid", "completed"];
      return (
        moment().diff(moment(item.created_at), "day") <= 60 &&
        refundable.includes(item.status)
      );
    },
    isSmartCard(item) {
      return item.payment_method == "smart_card";
    },
    isAutoRenew(item) {
      return (
        item.charge_type === "signature" &&
        item.subscription &&
        item.subscription.cycles === "Renova até o cliente cancelar!"
      );
    },

    itemActions(item) {
      const actions = [];

      actions.push({
        label: "Detalhes",
        click: this.openDetail
      });

      if (
        this.$permissions.hasPermission("solicitar-reembolsos") &&
        this.allowRefund(item)
      ) {
        actions.push({
          label: "Reembolsar",
          variant: "danger",
          class: "text-danger",
          dialog: {
            text: `Deseja mesmo realizar o reembolso da transação: BP${item.id}?`,
            icon: "ph ph-arrows-counter-clockwise",
            buttonText: "Sim, confirmar",
            action: i => this.submitRefund(i.id),
            secondaryButtonText: "Não, cancelar"
          }
        });
      }

      if (item.billet) {
        actions.push({
          label: "Abrir boleto",
          click: this.showBillet
        });
      } else if (item.payment_method === "pix" && item.status === "pending") {
        actions.push({
          label: "Reenviar pix",
          dialog: {
            text: "Deseja reenviar o pix?",
            buttonText: "Confirmar",
            secondaryButtonText: "Cancelar",
            action: this.rePix,
            icon: "fa fa-circle-exclamation"
          }
        });
      } else if (item.status === "failed") {
        actions.push({
          label: "Tentar novamente",
          click: this.tryAgain
        });
      }

      actions.push({
        label: "Comprovante",
        click: this.openReceipt
      });

      if (item.charge_type === "signature" && item.status !== "failed") {
        if (this.$permissions.hasPermission("gerenciar-assinaturas")) {
          actions.push({
            label: "Configurar pagamento",
            click: i => this.changeSubscription(i.id)
          });
        }
        if (this.$permissions.hasPermission("cancelar-assinaturas")) {
          actions.push({
            label: "Cancelar assinatura",
            class: "text-danger",
            variant: "danger",
            dialog: {
              text: `Deseja mesmo realizar o cancelamento da assinatura de ${item.shopper_name} no produto ${item.product_name}?`,
              icon: "ph ph-x-circle",
              action: i => this.cancelSubscription(i.id),
              buttonText: "Sim, cancelar",
              secondaryButtonText: "Não, voltar"
            }
          });
        }
      }
      return actions;
    },

    closeModal() {
      this.modal.value = false;
    }
  },
  mounted() {
    this.$store.dispatch("integration/getCoproducerProducts", { limit: 1000 });

    const saved = this.$store.state.sale.salesFilters;

    if (saved) {
      this.filters = JSON.parse(JSON.stringify(saved.filters));
      this.product_type = saved.product_type;
    }

    this.dispatch();
  }
};
</script>
<style lang="scss">
.sales-base {
  .shopper-email {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
